import "./section-four-filters.scss";
import { type Dispatch, type SetStateAction, useEffect, useMemo, useState } from "react";
import {
  FiltersMenuDate,
  FiltersMenuRow,
  FiltersMenuTags,
  JournalActiveFilterChips,
  JournalFiltersMenu,
  formatJournalFilterDate,
  type JournalActiveFilterChip,
} from "@/features/journal-filters-menu";
import { FiltersButton, InputSearchWithInfo } from "@shared/ui";

// BUILDDOCS-4959: скрыто до реализации на бэкенде
// const DISPLAY_OPTIONS = [
//   { id: "FAVORITE", label: "Избранное" },
//   { id: "ACTIVE", label: "Активное" },
//   { id: "DELETED", label: "Удаленное" },
//   { id: "ALL", label: "Все" },
// ];

const STATUS_OPTIONS = [
  { id: "NEW", label: "Новый" },
  { id: "AWAITING_SIGNATURE", label: "На подписании" },
  { id: "SIGNED", label: "Подписан" },
  { id: "SENT_TO_GSN", label: "Отправлено" },
  { id: "REJECTED", label: "Отклонено" },
];

// BUILDDOCS-4959: скрыто до реализации на бэкенде
// const DEFECTS_OPTIONS = [
//   { id: "true", label: "Выявлены" },
//   { id: "false", label: "Не выявлены" },
// ];

type Signer = {
  constructionControlId?: string;
  user?: {
    userId?: string;
    surname?: string;
    firstName?: string;
    middleName?: string;
  };
};

type Draft = {
  display: string[];
  statuses: string[];
  signers: string[];
  defects: string[];
  creationDate: string | null;
  controlPeriod: [string, string];
  defectDeadline: string | null;
  eliminationDate: string | null;
  lastChangeDate: string | null;
};

const emptyDraft = (): Draft => ({
  display: [],
  statuses: [],
  signers: [],
  defects: [],
  creationDate: null,
  controlPeriod: ["", ""],
  defectDeadline: null,
  eliminationDate: null,
  lastChangeDate: null,
});

const formatSignerLabel = (signer: Signer) => {
  const surname = signer.user?.surname || "";
  const firstInitial = signer.user?.firstName ? `${signer.user.firstName[0]}.` : "";
  const middleInitial = signer.user?.middleName ? `${signer.user.middleName[0]}.` : "";
  return `${surname} ${firstInitial}${middleInitial}`.trim();
};

const getSignerId = (signer: Signer) =>
  signer.constructionControlId ?? signer.user?.userId ?? "";

export const SectionFourFilters = ({
  onSearchChange,
  selectedStatuses,
  setSelectedStatuses,
  signerIds,
  setSignerIds,
  controlPeriodStart,
  controlPeriodEnd,
  setControlPeriodStart,
  setControlPeriodEnd,
  defectDeadline,
  setDefectDeadline,
  eliminationDate,
  setEliminationDate,
  createDate,
  setCreateDate,
  updateDate,
  setUpdateDate,
  defectsFilter,
  setDefectsFilter,
  signersList,
  onFiltersApply,
  bulkSendMode = false,
}: {
  onSearchChange: (value: string) => void;
  selectedStatuses: string[];
  setSelectedStatuses: Dispatch<SetStateAction<string[]>>;
  signerIds: string[];
  setSignerIds: Dispatch<SetStateAction<string[]>>;
  controlPeriodStart: string;
  controlPeriodEnd: string;
  setControlPeriodStart: Dispatch<SetStateAction<string>>;
  setControlPeriodEnd: Dispatch<SetStateAction<string>>;
  defectDeadline: string;
  setDefectDeadline: Dispatch<SetStateAction<string>>;
  eliminationDate: string;
  setEliminationDate: Dispatch<SetStateAction<string>>;
  createDate: string;
  setCreateDate: Dispatch<SetStateAction<string>>;
  updateDate: string;
  setUpdateDate: Dispatch<SetStateAction<string>>;
  defectsFilter: string;
  setDefectsFilter: Dispatch<SetStateAction<string>>;
  signersList: Signer[];
  onFiltersApply?: () => void;
  bulkSendMode?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [applied, setApplied] = useState<Draft>(emptyDraft);
  const [draft, setDraft] = useState<Draft>(emptyDraft);

  const signerOptions = useMemo(
    () =>
      signersList
        .map((signer) => ({
          id: getSignerId(signer),
          label: formatSignerLabel(signer),
        }))
        .filter((item) => item.id),
    [signersList],
  );

  const hasActiveFilters =
    (!bulkSendMode && selectedStatuses.length > 0) ||
    signerIds.length > 0 ||
    Boolean(defectDeadline || eliminationDate);
    // Boolean(defectsFilter) ||
    // Boolean(createDate || updateDate || controlPeriodStart || controlPeriodEnd) ||
    // applied.display.length > 0;

  useEffect(() => {
    setApplied((prev) => ({
      ...prev,
      statuses: selectedStatuses,
      signers: signerIds,
      defects: defectsFilter ? [defectsFilter] : [],
      creationDate: createDate || null,
      lastChangeDate: updateDate || null,
      controlPeriod:
        controlPeriodStart || controlPeriodEnd
          ? [controlPeriodStart, controlPeriodEnd]
          : ["", ""],
      defectDeadline: defectDeadline || null,
      eliminationDate: eliminationDate || null,
    }));
  }, [
    selectedStatuses,
    signerIds,
    defectsFilter,
    createDate,
    updateDate,
    controlPeriodStart,
    controlPeriodEnd,
    defectDeadline,
    eliminationDate,
  ]);

  const openModal = () => {
    setDraft({
      display: applied.display,
      statuses: selectedStatuses,
      signers: signerIds,
      defects: defectsFilter ? [defectsFilter] : [],
      creationDate: createDate || null,
      lastChangeDate: updateDate || null,
      controlPeriod:
        controlPeriodStart || controlPeriodEnd
          ? [controlPeriodStart, controlPeriodEnd]
          : ["", ""],
      defectDeadline: defectDeadline || null,
      eliminationDate: eliminationDate || null,
    });
    setOpen(true);
  };

  const applyFilters = () => {
    setApplied(draft);
    setSelectedStatuses(draft.statuses);
    setSignerIds(draft.signers);
    setDefectsFilter(draft.defects[0] || "");
    setCreateDate(draft.creationDate || "");
    setUpdateDate(draft.lastChangeDate || "");
    setControlPeriodStart(draft.controlPeriod[0]);
    setControlPeriodEnd(draft.controlPeriod[1]);
    setDefectDeadline(draft.defectDeadline || "");
    setEliminationDate(draft.eliminationDate || "");
    setOpen(false);
    onFiltersApply?.();
  };

  const resetFilters = () => {
    const cleared = emptyDraft();
    setDraft(cleared);
    setApplied(cleared);
    setSelectedStatuses([]);
    setSignerIds([]);
    setDefectsFilter("");
    setCreateDate("");
    setUpdateDate("");
    setControlPeriodStart("");
    setControlPeriodEnd("");
    setDefectDeadline("");
    setEliminationDate("");
    setOpen(false);
    onFiltersApply?.();
  };

  return (
    <div className="section-four-filters">
      <InputSearchWithInfo
        className="section-four-filters__search"
        style={{ fontSize: "12px" }}
        onChange={(e) => onSearchChange(e.target.value)}
        tooltipTitle="Поиск по сведениям о проведении строй контроля"
      />
      <FiltersButton active={hasActiveFilters} onClick={openModal} />

      <JournalFiltersMenu
        open={open}
        onClose={() => setOpen(false)}
        onApply={applyFilters}
        onReset={resetFilters}
      >
        {/* BUILDDOCS-4959: скрыто до реализации на бэкенде
        <FiltersMenuRow label="Отображение">
          <FiltersMenuTags
            options={DISPLAY_OPTIONS}
            value={draft.display}
            onChange={(display) => setDraft((prev) => ({ ...prev, display }))}
          />
        </FiltersMenuRow>
        */}

        {!bulkSendMode ? (
          <FiltersMenuRow
            stacked
            label="Статус записи"
            onClear={
              draft.statuses.length
                ? () => setDraft((prev) => ({ ...prev, statuses: [] }))
                : undefined
            }
          >
            <FiltersMenuTags
              options={STATUS_OPTIONS}
              value={draft.statuses}
              onChange={(statuses) => setDraft((prev) => ({ ...prev, statuses }))}
            />
          </FiltersMenuRow>
        ) : null}

        <FiltersMenuRow
          stacked
          label="Подписант"
          onClear={
            draft.signers.length
              ? () => setDraft((prev) => ({ ...prev, signers: [] }))
              : undefined
          }
        >
          <FiltersMenuTags
            options={signerOptions}
            value={draft.signers}
            onChange={(signers) => setDraft((prev) => ({ ...prev, signers }))}
          />
        </FiltersMenuRow>

        {/* BUILDDOCS-4959: скрыто до реализации на бэкенде
        <FiltersMenuRow label="Дата создания">
          <FiltersMenuDate
            value={draft.creationDate}
            onChange={(creationDate) =>
              setDraft((prev) => ({
                ...prev,
                creationDate: creationDate as string | null,
              }))
            }
          />
        </FiltersMenuRow>

        <FiltersMenuRow label="Сроки проведения">
          <FiltersMenuDate
            range
            value={draft.controlPeriod}
            onChange={(controlPeriod) =>
              setDraft((prev) => ({
                ...prev,
                controlPeriod: (controlPeriod as [string, string]) || ["", ""],
              }))
            }
          />
        </FiltersMenuRow>
        */}

        <FiltersMenuRow label="Срок устранения недостатков">
          <FiltersMenuDate
            value={draft.defectDeadline}
            onChange={(defectDeadlineValue) =>
              setDraft((prev) => ({
                ...prev,
                defectDeadline: defectDeadlineValue as string | null,
              }))
            }
          />
        </FiltersMenuRow>

        <FiltersMenuRow label="Дата фактического устранения">
          <FiltersMenuDate
            value={draft.eliminationDate}
            onChange={(eliminationDateValue) =>
              setDraft((prev) => ({
                ...prev,
                eliminationDate: eliminationDateValue as string | null,
              }))
            }
          />
        </FiltersMenuRow>

        {/* BUILDDOCS-4959: скрыто до реализации на бэкенде
        <FiltersMenuRow label="Дата последнего изменения">
          <FiltersMenuDate
            value={draft.lastChangeDate}
            onChange={(lastChangeDate) =>
              setDraft((prev) => ({
                ...prev,
                lastChangeDate: lastChangeDate as string | null,
              }))
            }
          />
        </FiltersMenuRow>

        <FiltersMenuRow label="Недостатки">
          <FiltersMenuTags
            single
            options={DEFECTS_OPTIONS}
            value={draft.defects}
            onChange={(defects) => setDraft((prev) => ({ ...prev, defects }))}
          />
        </FiltersMenuRow>
        */}
      </JournalFiltersMenu>
    </div>
  );
};

export const SectionFourActiveFilterChips = ({
  selectedStatuses,
  setSelectedStatuses,
  signerIds,
  setSignerIds,
  defectDeadline,
  setDefectDeadline,
  eliminationDate,
  setEliminationDate,
  signersList,
  onFilterRemove,
  bulkSendMode = false,
}: {
  selectedStatuses: string[];
  setSelectedStatuses: Dispatch<SetStateAction<string[]>>;
  signerIds: string[];
  setSignerIds: Dispatch<SetStateAction<string[]>>;
  defectDeadline: string;
  setDefectDeadline: Dispatch<SetStateAction<string>>;
  eliminationDate: string;
  setEliminationDate: Dispatch<SetStateAction<string>>;
  signersList: Signer[];
  onFilterRemove?: () => void;
  bulkSendMode?: boolean;
}) => {
  const signerOptions = useMemo(
    () =>
      signersList
        .map((signer) => ({
          id: getSignerId(signer),
          label: formatSignerLabel(signer),
        }))
        .filter((item) => item.id),
    [signersList],
  );

  const removeFilter = (updater: () => void) => {
    updater();
    onFilterRemove?.();
  };

  const chips: JournalActiveFilterChip[] = [];

  if (!bulkSendMode) {
    selectedStatuses.forEach((statusId) => {
      const option = STATUS_OPTIONS.find((item) => item.id === statusId);
      if (!option) return;
      chips.push({
        key: `status-${statusId}`,
        filterLabel: "Статус записи",
        value: option.label,
        onRemove: () =>
          removeFilter(() =>
            setSelectedStatuses((prev) => prev.filter((item) => item !== statusId)),
          ),
      });
    });
  }

  signerIds.forEach((signerId) => {
    const option = signerOptions.find((item) => item.id === signerId);
    if (!option) return;
    chips.push({
      key: `signer-${signerId}`,
      filterLabel: "Подписант",
      value: option.label,
      onRemove: () =>
        removeFilter(() =>
          setSignerIds((prev) => prev.filter((item) => item !== signerId)),
        ),
    });
  });

  if (defectDeadline) {
    chips.push({
      key: "defect-deadline",
      filterLabel: "Срок устранения недостатков",
      value: formatJournalFilterDate(defectDeadline),
      onRemove: () => removeFilter(() => setDefectDeadline("")),
    });
  }

  if (eliminationDate) {
    chips.push({
      key: "elimination-date",
      filterLabel: "Дата фактического устранения",
      value: formatJournalFilterDate(eliminationDate),
      onRemove: () => removeFilter(() => setEliminationDate("")),
    });
  }

  return <JournalActiveFilterChips chips={chips} />;
};
